<template>
  <Teleport to="body">
    <div
      v-for="item in openLoginPopNoticeItems"
      :key="item.id"
      class="login-pop-notice"
      :class="{ 'is-dragging': draggingId === item.id }"
      :style="getPopupStyle(item)"
      @mousedown="bringToFront(item.id)"
    >
      <div class="login-pop-notice__header" @mousedown="e => startDrag(e, item)">
        <h3 class="login-pop-notice__title">{{ item.notice?.subject || '공지사항' }}</h3>
        <button type="button" class="login-pop-notice__close" title="닫기" @click="closeLoginPopNotice(item.id)">
          <Icon name="heroicons:x-mark" size="18" />
        </button>
      </div>
      <div class="login-pop-notice__body" v-html="item.notice?.content || ''" />

      <div v-if="item.atchFileKey || item.detailUrl" class="login-pop-notice__extra">
        <table class="login-pop-notice__extra-table">
          <colgroup>
            <col style="width: 20%" />
            <col style="width: 80%" />
          </colgroup>
          <tbody>
            <tr v-if="item.atchFileKey">
              <th>첨부파일</th>
              <td>
                <UiFileAttachment v-model="item.attachmentFiles" :isFileUploadDisabled="true" />
              </td>
            </tr>
            <tr v-if="item.detailUrl">
              <th>상세 URL</th>
              <td>
                <a :href="item.detailUrl" target="_blank" rel="noopener noreferrer">{{ item.detailUrl }}</a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="login-pop-notice__footer">
        <UiCheckbox
          :model-value="item.doNotShowAgain"
          label="다시 보지 않음"
          size="medium"
          @update:model-value="checked => onDoNotShowLoginPopNoticeChange(item.id, checked)"
        />
        <UiButton variant="secondary" size="small" @click="closeLoginPopNotice(item.id)">닫기</UiButton>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { onBeforeUnmount, reactive, ref } from 'vue';
import UiFileAttachment from '~/components/attachment/UiFileAttachment.vue';
import { useNoticeBoardStore } from '~/composables/user/useNoticeBoardStore';

const {
  openLoginPopNoticeItems,
  onDoNotShowLoginPopNoticeChange,
  closeLoginPopNotice,
} = useNoticeBoardStore();

const draggingId = ref(null);
const positions = reactive({}); // { [id]: { x, y } }
let dragState = null;

const toCssSize = (value, fallback) => {
  if (value == null || value === '') return fallback;
  const str = String(value).trim().toLowerCase();
  if (!str) return fallback;

  // "800", "800px", "800.5" 등 숫자 추출
  const matched = str.match(/^(\d+(?:\.\d+)?)\s*(px)?$/);
  if (!matched) return fallback;

  const num = Number(matched[1]);
  return Number.isFinite(num) && num > 0 ? Math.round(num) : fallback;
};

const getSize = item => {
  const notice = item?.notice ?? {};
  const width = Math.min(toCssSize(notice.width, 800), window.innerWidth - 40);
  const height = Math.min(toCssSize(notice.height, 450), window.innerHeight - 40);
  return { width, height };
};

const ensurePosition = item => {
  if (positions[item.id]) return positions[item.id];

  const { width, height } = getSize(item);
  const offset = (item.offsetIndex || 0) * 48;
  const x = Math.max(20, Math.round((window.innerWidth - width) / 2) + offset);
  const y = Math.max(20, Math.round((window.innerHeight - height) / 2) + offset);
  positions[item.id] = { x, y };
  return positions[item.id];
};

const getPopupStyle = item => {
  const { width, height } = getSize(item);
  const pos = ensurePosition(item);

  return {
    width: `${width}px`,
    height: `${height}px`,
    left: `${pos.x}px`,
    top: `${pos.y}px`,
    zIndex: item.zIndex,
  };
};

const bringToFront = noticeId => {
  const maxZ = openLoginPopNoticeItems.value.reduce((max, item) => Math.max(max, item.zIndex || 0), 1200);
  const item = openLoginPopNoticeItems.value.find(x => x.id === String(noticeId));
  if (item && item.zIndex <= maxZ) {
    item.zIndex = maxZ + 1;
  }
};

const startDrag = (e, item) => {
  if (e.target.closest('.login-pop-notice__close')) return;

  e.preventDefault();
  bringToFront(item.id);

  const pos = ensurePosition(item);
  draggingId.value = item.id;
  dragState = {
    id: item.id,
    startX: e.clientX,
    startY: e.clientY,
    originX: pos.x,
    originY: pos.y,
  };

  document.addEventListener('mousemove', onDragMove);
  document.addEventListener('mouseup', onDragEnd);
};

const onDragMove = e => {
  if (!dragState) return;

  const { width, height } = getSize(
    openLoginPopNoticeItems.value.find(x => x.id === dragState.id) || { notice: {} },
  );
  const nextX = dragState.originX + (e.clientX - dragState.startX);
  const nextY = dragState.originY + (e.clientY - dragState.startY);

  positions[dragState.id] = {
    x: Math.min(Math.max(0, nextX), Math.max(0, window.innerWidth - width)),
    y: Math.min(Math.max(0, nextY), Math.max(0, window.innerHeight - height)),
  };
};

const onDragEnd = () => {
  draggingId.value = null;
  dragState = null;
  document.removeEventListener('mousemove', onDragMove);
  document.removeEventListener('mouseup', onDragEnd);
};

onBeforeUnmount(() => {
  onDragEnd();
});
</script>

<style lang="scss" scoped>
.login-pop-notice {
  position: fixed;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  max-width: calc(100vw - 40px);
  max-height: calc(100vh - 40px);

  &.is-dragging {
    user-select: none;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 12px 16px;
    border-bottom: 1px solid #e8eaed;
    cursor: move;
    flex-shrink: 0;
    background: #fff;
  }

  &__title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: 0;
    background: transparent;
    border-radius: 4px;
    cursor: pointer;
    color: #58616a;
    flex-shrink: 0;

    &:hover {
      background: #f3f4f6;
    }
  }

  &__body {
    flex: 1 1 auto;
    min-height: 0;
    margin: 12px 16px 0;
    padding: 10px 12px;
    border-radius: 8px;
    background: #f8fafc;
    overflow-y: auto;
    overflow-x: hidden;
    font-size: 14px;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;
    scrollbar-width: thin;
    scrollbar-color: rgba(200, 200, 200, 0.5) transparent;

    &::-webkit-scrollbar {
      display: block;
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(200, 200, 200, 0.5);
      border-radius: 3px;

      &:hover {
        background: rgba(180, 180, 180, 0.7);
      }
    }
  }

  &__extra {
    flex-shrink: 0;
    padding: 12px 16px 0;
  }

  &__extra-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th,
    td {
      border: 1px solid #e8eaed;
      padding: 10px 12px;
      vertical-align: top;
    }

    th {
      background: #f1f3f5;
      text-align: left;
      white-space: nowrap;
      font-weight: 600;
    }

    a {
      color: $primary-color;
      word-break: break-all;
      text-decoration: underline;
    }
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 16px;
    border-top: 1px solid #e8eaed;
    flex-shrink: 0;
  }
}
</style>
